import { AssigneeType } from "./../../assignees/models";
import { IScope, IRootScopeService, ITimeoutService, IPromise } from "angular";
import { ICustomField } from "@gtmhub/customFields";
import { UIErrorHandlingService } from "@gtmhub/error-handling";
import { GoalModalsService } from "@gtmhub/goals/goalModalsService";
import { GoalService } from "@gtmhub/goals/goal.service";
import { IGoal, SingleGoalSelectorInvokedBy } from "@gtmhub/goals/models";
import { ICollection } from "@gtmhub/models";
import { IMetric } from "@gtmhub/okrs/metrics";
import { ITask, TaskService } from "..";
import { TaskEvents } from "../taskEvents";
import { handleOutsideMouseEvent } from "@gtmhub/shared/directives/close-on-mousedown";
import { formatSearchAssignees, IAssignee } from "@gtmhub/assignees";
import { GoalsChangedEventArgs, goalSelected, metricSelected, OkrEvents } from "@gtmhub/okrs";
import { SearchService } from "@gtmhub/shared/services/search.service";
import { SearchOperatorsEnum } from "@webapp/search/models/search-api.models";
import { Search } from "@webapp/search/models/search.models";

interface ITaskFormScope extends IScope {
  task: ITask;
  dueDate: string;
  selectedParent: IGoal | IMetric;
  customFields: ICollection<ICustomField>;
  indicators: {
    loading: boolean;
    saving: boolean;
    selectingAssignee: boolean;
  };
  removeParent(): void;
  keyup(event: KeyboardEvent, field: string): void;
  getAssignees(nameFilter: string);
  selectNewAssignee(assignee: IAssignee);
  typeaheadBlur();
  setDueDate(date: string): void;
  removeDueDate(): void;
  openGoalSelector(): void;
}

interface ITaskFormStateParams {
  taskFormId: string;
  parentId: string;
}
export class TaskFormCtrl {
  private taskOwnerType = "user";
  private taskEvents: TaskEvents;
  private okrEvents: OkrEvents;

  public static $inject = [
    "$rootScope",
    "$scope",
    "$stateParams",
    "GoalService",
    "GoalModalsService",
    "TaskService",
    "UIErrorHandlingService",
    "$timeout",
    "SearchService",
  ];

  constructor(
    private $rootScope: IRootScopeService,
    private $scope: ITaskFormScope,
    private $stateParams: ITaskFormStateParams,
    private goalService: GoalService,
    private goalModalsService: GoalModalsService,
    private tasksService: TaskService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private $timeout: ITimeoutService,
    private searchService: SearchService
  ) {
    this.taskEvents = new TaskEvents(this.$scope);
    this.okrEvents = new OkrEvents(this.$scope);
    this.okrEvents.onGoalsChanged((args) => this.onGoalsChanged(args));

    this.$scope.keyup = this.keyup;
    this.$scope.getAssignees = this.getAssignees;
    this.$scope.selectNewAssignee = this.selectNewAssignee;
    this.$scope.typeaheadBlur = this.typeaheadBlur;
    this.$scope.removeParent = this.removeParent;
    this.$scope.openGoalSelector = this.openGoalSelector;

    this.$scope.indicators = {
      loading: true,
      saving: false,
      selectingAssignee: false,
    };

    this.$scope.setDueDate = this.setDueDate;
    this.$scope.removeDueDate = this.removeDueDate;
    this.$scope.selectedParent = {
      sessionId: "",
    };

    this.init();
  }

  private init = () => {
    if (this.$stateParams.taskFormId) {
      this.tasksService.getOne(this.$stateParams.taskFormId).then(
        (task) => {
          this.$scope.task = task;
          this.$scope.indicators.loading = false;
          this.checkParent();
        },
        (error) => this.uiErrorHandlingService.handleIndicator(error, { indicator: "loading" })
      );
    } else {
      this.$scope.indicators.loading = false;

      this.$scope.task = {
        ownerId: "",
        description: "",
        name: "",
        status: "todo",
        customFields: {},
      };

      if (this.$stateParams.parentId) {
        this.$scope.task.parentId = this.$stateParams.parentId;
        this.checkParent();
      }

      this.bindOutsideClickToCloseForm();
      this.setAutofocus();
    }
  };

  private setDueDate = (date: string): void => {
    delete this.$scope.task.unset; // this needs to be deleted otherwise the backend return 500
    this.$scope.dueDate = date;
    this.$scope.task.dueDate = date;
  };

  private removeDueDate = (): void => {
    this.$scope.task.unset = {
      dueDate: true,
    };
    this.$scope.dueDate = "";
    delete this.$scope.task.dueDate; // this needs to be deleted otherwise the backend return 500
  };

  private checkParent = () => {
    if (this.$scope.task.parentId) {
      this.getParentGoal(this.$scope.task.parentId);
    }
  };

  private removeParent = () => {
    this.$scope.task.parentId = "";
    delete this.$scope.selectedParent;
  };

  private openGoalSelector = (): void => {
    this.goalModalsService.openGoalSelectModal({ unselectableId: this.$scope.task.parentId, invokedById: this.$scope.task.id, invokedBy: "TaskKanban" });
  };

  private getParentGoal = (id) => {
    this.$scope.indicators.loading = true;
    this.goalService.getGoal(id).then(
      (goal) => {
        this.$scope.indicators.loading = false;
        this.$scope.selectedParent = {
          name: goal.name,
          ownerIds: goal.ownerIds,
          sessionId: goal.sessionId,
        };
      },
      (error) => this.uiErrorHandlingService.handleIndicator(error, { indicator: "loading" })
    );
  };

  private onGoalsChanged = (args: GoalsChangedEventArgs): void => {
    if (args.reason === metricSelected) {
      this.metricSelected(args.detail.metric, args.detail.invokedBy);
      return;
    }
    if (args.reason === goalSelected) {
      this.goalSelected(args.detail.goal, args.detail.invokedBy);
    }
  };

  private goalSelected = (goal: IGoal, invokedBy: SingleGoalSelectorInvokedBy) => {
    if (invokedBy === "TaskKanban") {
      this.$scope.task.parentId = goal.id;
      this.$scope.task.parentType = "goal";
      this.$scope.selectedParent = {
        name: goal.name,
        ownerIds: goal.ownerIds,
        sessionId: goal.sessionId,
      };
    }
  };

  private metricSelected = (metric: IMetric, invokedBy: SingleGoalSelectorInvokedBy) => {
    if (invokedBy === "TaskKanban") {
      this.$scope.task.parentId = metric.id;
      this.$scope.task.parentType = "metric";
      this.$scope.selectedParent = {
        name: metric.name,
        ownerIds: metric.ownerIds,
        sessionId: metric.sessionId,
      };
    }
  };

  private create = () => {
    this.$scope.indicators.saving = true;
    this.tasksService.create(this.$scope.task).then(
      (createdTask) => {
        this.$scope.indicators.saving = false;
        let sessionId = null;
        if (this.$scope.selectedParent && this.$scope.selectedParent.sessionId) {
          sessionId = this.$scope.selectedParent.sessionId;
        }

        this.taskEvents.broadcastTaskCreated({ task: createdTask, sessionId });
      },
      (error) => this.uiErrorHandlingService.handleIndicator(error, { indicator: "saving" })
    );
  };

  private keyup = (event: KeyboardEvent, field: string): void => {
    if (event.key === "Enter" && this.$scope.task.name) {
      this.create();
    }

    if (event.key === "Tab") {
      const inputEl = document.querySelector<HTMLTextAreaElement>(`#task${this.$scope.task.id} [ng-model='task.${field}']`);
      if (inputEl) {
        inputEl.selectionStart = inputEl.selectionEnd;
      }
    }
  };

  private bindOutsideClickToCloseForm = (): void => {
    const unsubscribe = handleOutsideMouseEvent(
      "mousedown",
      () => {
        if (this.$scope.task.name) {
          this.create();
        } else {
          this.$rootScope.$broadcast("closeFormTaskInKanban");
        }

        unsubscribe();
      },

      "#task-form-kanban",
      "#create-new-kanban-task",
      "single-goal-selector"
    );
    this.$scope.$on("$destroy", unsubscribe);
  };

  private getAssignees = (searchTerm: string): IPromise<IAssignee[]> => {
    const searchRequests = this.searchService.getAssigneeSearchCollections(this.taskOwnerType as AssigneeType, searchTerm);
    const searchBody = {
      searchTerm,
      operator: searchTerm ? SearchOperatorsEnum.matchPrefix : SearchOperatorsEnum.matchAny,
      searchRequests,
    };

    return this.searchService.getSomeSearchData<Search<"teams"> | Search<"users">>(searchBody).then(
      (response) => formatSearchAssignees(response.items),
      () => []
    );
  };

  private selectNewAssignee = (assignee: IAssignee) => {
    this.$scope.task.ownerId = assignee.id;
    this.$scope.indicators.selectingAssignee = true;

    this.$timeout(() => {
      (document.getElementById("inputFieldSelectNewAssignee") as HTMLInputElement).value = "";

      this.$timeout(() => {
        document.getElementById("inputFieldSelectNewAssignee").blur();
        this.$scope.indicators.selectingAssignee = false;
      }, 0);
    }, 0);
  };

  private typeaheadBlur = () => {
    this.$timeout(() => {
      (document.getElementById("inputFieldSelectNewAssignee") as HTMLInputElement).value = "";
    }, 0);
  };

  private setAutofocus = () => {
    this.$timeout(() => {
      this.$scope.$broadcast("focusTextInput");
    }, 0);
  };
}
